import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { EntityType } from "@mapmycustomers/shared/enum";
import User from "@mapmycustomers/shared/types/User";
import userDisplayName from "@mapmycustomers/shared/util/formatters/userDisplayName";

import { getUsersOfEntireOrganization } from "@app/store/members";
import TextSearchResult from "@app/types/TextSearchResult";

import { clearEntityMentionSearch, searchEntityMentions } from "../store/actions";
import {
  getEntityMentionSearchedQuery,
  getEntityMentionSearchLoading,
  getEntityMentionSearchResults,
} from "../store/selectors";

const USER_CAP = 30;

const useEntityMentionSearch = (query: string) => {
  const dispatch = useDispatch();
  const allUsers = useSelector(getUsersOfEntireOrganization);
  const searchLoading = useSelector(getEntityMentionSearchLoading);
  const searchedQuery = useSelector(getEntityMentionSearchedQuery);
  const entityResults = useSelector(getEntityMentionSearchResults);

  const userMatches = useMemo<TextSearchResult[]>(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery || !allUsers) {
      return [];
    }
    // One person can have multiple user records sharing a username (e.g. a
    // re-invited account): one with a fullName, one with only the email. Keep
    // a single record per username, preferring the named one, so the dropdown
    // shows the user once and the surviving record resolves to a real name
    // rather than a stale id that renders as a raw @user:<id> on select.
    const byUsername = new Map<string, User>();
    allUsers.forEach((user) => {
      const key = (user.username ?? "").toLowerCase();
      const existing = byUsername.get(key);
      if (!existing || (!existing.fullName && user.fullName)) {
        byUsername.set(key, user);
      }
    });
    return [...byUsername.values()]
      .filter((user) => {
        const name = userDisplayName(user).toLowerCase();
        const email = (user.username ?? "").toLowerCase();
        return name.includes(normalizedQuery) || email.includes(normalizedQuery);
      })
      .slice(0, USER_CAP)
      .map(
        (user): TextSearchResult => ({
          entity: EntityType.USER,
          entityId: user.id,
          metaFields: "",
          name: userDisplayName(user),
          rank: 0,
        })
      );
  }, [allUsers, query]);

  useEffect(() => {
    if (query.length >= 1) {
      dispatch(searchEntityMentions.request({ query }));
    } else {
      dispatch(clearEntityMentionSearch());
    }
  }, [dispatch, query]);

  const results = useMemo<TextSearchResult[]>(() => {
    // Users come from userMatches; drop server USER hits to avoid duplicate,
    // unresolvable entries.
    const serverResults = entityResults.filter(({ entity }) => entity !== EntityType.USER);
    return [...serverResults, ...userMatches];
  }, [entityResults, userMatches]);

  const loading = query.length >= 1 && (searchLoading || searchedQuery !== query);

  const reset = useCallback(() => {
    dispatch(clearEntityMentionSearch());
  }, [dispatch]);

  return { loading, reset, results };
};

export default useEntityMentionSearch;
